// Geometry section (position, rotation, align to label) and the multi-selection panel.
import { h } from '../dom.js';
import { S, selectedFields, editableSelection, isLocked } from '../state.js';
import * as A from '../actions.js';
import { fieldBounds, unionBounds } from '../geometry.js';
import { section, row, full, mmInput, segmented, iconButton, hint } from './controls.js';

const ROTATIONS = [0, 90, 180, 270].map((v) => ({ value: v, label: `${v}°`, title: `Rotate to ${v}°` }));

const LABEL_ALIGN = [
  ['left', 'Align to left edge of label', 'M5 4v16M9 7h12M9 12h8M9 17h12'],
  ['cx', 'Centre horizontally on label', 'M12 3v18M6 8h12M9 16h6'],
  ['right', 'Align to right edge of label', 'M19 4v16M3 7h12M7 12h8M3 17h12'],
  ['top', 'Align to top edge of label', 'M4 5h16M7 9v12M12 9v8M17 9v12'],
  ['cy', 'Centre vertically on label', 'M3 12h18M8 6v12M16 9v6'],
  ['bottom', 'Align to bottom edge of label', 'M4 19h16M7 3v12M12 3v8M17 3v12'],
];

function alignToLabelGrid() {
  return h(
    'div',
    { style: { display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '4px' }, role: 'group', 'aria-label': 'Align to label' },
    LABEL_ALIGN.map(([cmd, label, d]) => iconButton(label, `<path d="${d}"/>`, () => A.alignToLabel(cmd))),
  );
}

export function geometrySection(b, ctx) {
  const f = () => ctx.cur();
  return section('Position', 'geo', [
    row('X', mmInput(b, { ariaLabel: 'X position (mm)', get: () => f()?.x, set: (u) => ctx.edit({ x: u }, 'x'), min: 0, step: 0.1 }), { scrub: { step: 0.05 } }),
    row('Y', mmInput(b, { ariaLabel: 'Y position (mm)', get: () => f()?.y, set: (u) => ctx.edit({ y: u }, 'y'), min: 0, step: 0.1 }), { scrub: { step: 0.05 } }),
    row(
      'Rotation',
      h(
        'div',
        { style: { display: 'grid', gridTemplateColumns: '1fr auto', gap: '6px' } },
        segmented(b, { ariaLabel: 'Rotation', options: ROTATIONS, get: () => f()?.rotation || 0, set: (v) => ctx.edit({ rotation: v }) }),
        iconButton('Rotate 90° clockwise (R)', '<path d="M20 12a8 8 0 1 1-2.3-5.7"/><path d="M20 4v5h-5"/>', () => A.rotateSelected(90)),
      ),
    ),
    full(h('div', { class: 'hint', style: { marginBottom: '-2px' } }, 'Align to label'), alignToLabelGrid()),
  ]);
}

const MULTI_ALIGN = [
  ['align-l', 'Align left edges', 'M4 4v16M8 7h11M8 12h7M8 17h11'],
  ['align-cx', 'Align centres', 'M12 3v18M5 7h14M8 12h8M5 17h14'],
  ['align-r', 'Align right edges', 'M20 4v16M5 7h11M9 12h7M5 17h11'],
  ['align-t', 'Align top edges', 'M4 4h16M7 8v11M12 8v7M17 8v11'],
  ['align-cy', 'Align middles', 'M3 12h18M7 5v14M12 8v8M17 5v14'],
  ['align-b', 'Align bottom edges', 'M4 20h16M7 5v11M12 9v7M17 5v11'],
];

export function multiPanel(b) {
  const sel = selectedFields();
  const editable = editableSelection();
  const locked = sel.filter(isLocked).length;
  const grid = (items, cols = 3) =>
    h('div', { style: { display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: '4px' } }, items.map(([cmd, label, d]) => iconButton(label, `<path d="${d}"/>`, () => A.alignSelection(cmd))));
  const size = () => {
    const u = unionBounds(selectedFields().map((f) => fieldBounds(f)));
    return u ? `${(u.w / 100).toFixed(2)} × ${(u.h / 100).toFixed(2)} mm at ${(u.x / 100).toFixed(2)}, ${(u.y / 100).toFixed(2)} mm` : '—';
  };
  const readout = h('div', { class: 'readout' });
  b.add(() => {
    readout.textContent = size();
  });
  return h(
    'div',
    null,
    h('div', { class: 'props-head' }, h('div', { class: 'props-name' }, h('input', { value: `${sel.length} fields selected`, readonly: true, 'aria-label': 'Selection' })), readout),
    h(
      'div',
      { class: 'props-body', style: { padding: '0 var(--s4) var(--s4)' } },
      locked ? hint(`${locked} locked or unsupported field${locked === 1 ? '' : 's'} will not move.`, { paddingTop: '12px' }) : null,
      section('Align', 'multi-align', [full(grid(MULTI_ALIGN))]),
      section('Distribute', 'multi-dist', [
        full(grid([['dist-x', 'Distribute horizontally (equal gaps)', 'M4 4v16M20 4v16M10 8v8M14 8v8'], ['dist-y', 'Distribute vertically (equal gaps)', 'M4 4h16M4 20h16M8 10h8M8 14h8']], 2)),
        editable.length < 3 ? hint('Select three or more fields to distribute.') : null,
      ]),
      section('Align to label', 'multi-label', [full(alignToLabelGrid())]),
      section('Arrange', 'multi-arrange', [
        full(
          h(
            'div',
            { class: 'toggle-row' },
            h('button', { class: 'tog', type: 'button', onClick: () => A.rotateSelected(90), title: 'Rotate each field 90° (R)' }, 'Rotate 90°'),
            h('button', { class: 'tog', type: 'button', onClick: A.duplicateSelected }, 'Duplicate'),
            h('button', { class: 'tog', type: 'button', onClick: A.toggleLockSelected, title: 'Editor only — not saved to the file' }, 'Lock'),
            h('button', { class: 'tog', type: 'button', onClick: A.deleteSelected, style: { color: 'var(--bad)' } }, 'Delete'),
          ),
        ),
      ]),
    ),
  );
}

export { S };
